import React, {useState} from 'react';
import {Button, IconButton, Link, Stack, Typography} from '@mui/material';
import {AlertModal, CTAButtonFixed, IconIllustration, TagChip} from 'presentation/components';
import {ChipColorEnum} from 'presentation/components/TagChip/types';
import Icon from 'presentation/icons';
import {useCallChatRoom, useMediaQueries} from 'presentation/pages/private';
import {ChatRoomsEnum} from 'presentation/constants';
import {translations} from 'presentation/translations-new';
import {GuardianModel} from '../../constants';
import {FacialBiometricsHeader} from '../FacialBiometricsHeader';
import {ResponsiveCard} from '../ResponsiveCard';
import {GuardianAvatar, GuardianRow} from './styles';
import {GuardianSelectionProps} from './types';
import {useTheme} from 'styled-components';
import {useFacialBiometricsStore} from 'presentation/modules/booking/shared/store/useFacialBiometricsStore';
import {getInitials} from 'presentation/utils';

const {
	title,
	description,
	pendingChip,
	registeredChip,
	link,
	addGuardianModal,
	alreadyRegisteredGuardianModal,
	emptyState,
} = translations['pt-br'].others.facialBiometric.guardianSelection;

export function GuardianSelection({onNext, isLoading}: GuardianSelectionProps) {
	const theme = useTheme();
	const {isTablet} = useMediaQueries();
	const {callChatRoom} = useCallChatRoom();
	const guardians = useFacialBiometricsStore(state => state.guardians);
	const [showAddGuardianModal, setShowAddGuardianModal] = useState(false);
	const [registeredGuardian, setRegisteredGuardian] = useState<GuardianModel | null>(null);

	function handleSelectGuardian(guardian: GuardianModel) {
		if (guardian.facialRegistered) return;

		onNext(guardian);
	}

	function handleOpenAddGuardianModal() {
		setShowAddGuardianModal(true);
	}

	function handleCloseAddGuardianModal() {
		setShowAddGuardianModal(false);
	}

	async function handleConfirmAddGuardian() {
		setShowAddGuardianModal(false);
		await callChatRoom(ChatRoomsEnum.SAC);
	}

	function handleOpenRegisteredGuardianModal(guardian: GuardianModel) {
		setRegisteredGuardian(guardian);
	}

	function handleCloseRegisteredGuardianModal() {
		setRegisteredGuardian(null);
	}

	async function handleTalkToSupport() {
		setRegisteredGuardian(null);
		await callChatRoom(ChatRoomsEnum.SAC);
	}

	async function handleEmptyStateTalkToSupport() {
		await callChatRoom(ChatRoomsEnum.SAC);
	}

	if (guardians.length === 0) {
		return (
			<>
				<FacialBiometricsHeader />
				<Stack alignItems="center" height="100%">
					<ResponsiveCard>
						<Stack alignItems="center" gap={2} textAlign="center">
							<IconIllustration variant="primary" customIconName="person-outline" />
							<Typography variant="h6">{emptyState.title}</Typography>
							<Typography variant="body1" mb={1}>
								{emptyState.description}
							</Typography>
							{!isTablet && (
								<Button onClick={handleEmptyStateTalkToSupport} variant="contained">
									{emptyState.button}
								</Button>
							)}
						</Stack>
					</ResponsiveCard>

					{isTablet && (
						<CTAButtonFixed.Root $isTablet={isTablet} sx={{bottom: 0}}>
							<CTAButtonFixed.Action
								onClick={handleEmptyStateTalkToSupport}
								fullWidth>
								{emptyState.button}
							</CTAButtonFixed.Action>
						</CTAButtonFixed.Root>
					)}
				</Stack>
			</>
		);
	}

	return (
		<>
			<FacialBiometricsHeader />
			<Stack alignItems="center" height="100%">
				<ResponsiveCard>
					<Stack alignItems="center" gap={2}>
						<IconIllustration variant="primary" customIconName="person-outline" />
						<Typography variant="h6" textAlign="center">
							{title}
						</Typography>
						<Typography variant="body1" textAlign="center">
							{description}
						</Typography>
						<Stack
							direction={isTablet ? 'column' : 'row'}
							gap={2}
							width="100%"
							justifyContent="center">
							{guardians.map(guardian => {
								return (
									<GuardianRow
										key={guardian.personId}
										$disabled={guardian.facialRegistered}
										onClick={() => handleSelectGuardian(guardian)}>
										<Stack
											direction="row"
											alignItems="center"
											gap={1.5}
											width="100%">
											<GuardianAvatar $disabled={guardian.facialRegistered}>
												{getInitials(guardian.name)}
											</GuardianAvatar>
											<Stack width="100%">
												<Stack
													direction="row"
													alignItems="center"
													justifyContent="space-between"
													width="100%"
													gap={1}>
													<Typography variant="body1">
														{guardian.name}
													</Typography>
													{guardian.facialRegistered ? (
														<IconButton
															aria-label={registeredChip}
															onClick={event => {
																event.stopPropagation();
																handleOpenRegisteredGuardianModal(
																	guardian,
																);
															}}>
															<Icon
																name="info-outlined"
																color={
																	theme.colors.gray['50Percent']
																}
															/>
														</IconButton>
													) : (
														<IconButton
															aria-label={guardian.name}
															disabled={isLoading}>
															<Icon
																name="arrow-right"
																color={theme.palette.primary.main}
															/>
														</IconButton>
													)}
												</Stack>

												<TagChip
													label={
														guardian.facialRegistered
															? registeredChip
															: pendingChip
													}
													color={
														guardian.facialRegistered
															? ChipColorEnum.SUCCESS
															: ChipColorEnum.INFO
													}
													sx={{width: 'fit-content'}}
												/>
											</Stack>
										</Stack>
									</GuardianRow>
								);
							})}
						</Stack>
						{guardians.length === 1 && (
							<Button
								variant="text"
								size="small"
								onClick={handleOpenAddGuardianModal}>
								<Link>{link}</Link>
							</Button>
						)}
					</Stack>
				</ResponsiveCard>
			</Stack>
			<AlertModal
				open={showAddGuardianModal}
				onClose={handleCloseAddGuardianModal}
				variant="info"
				customIconName="info-filled"
				headerTitle={addGuardianModal.headerTitle}
				title={addGuardianModal.title}
				description={addGuardianModal.description}
				footerContent={
					<Stack direction="row" gap={2} width="100%">
						<Button
							onClick={handleCloseAddGuardianModal}
							variant="outlined"
							fullWidth
							size="large">
							{addGuardianModal.backButton}
						</Button>
						<Button
							onClick={handleConfirmAddGuardian}
							variant="contained"
							fullWidth
							size="large">
							{addGuardianModal.continueButton}
						</Button>
					</Stack>
				}
			/>
			<AlertModal
				open={Boolean(registeredGuardian)}
				onClose={handleCloseRegisteredGuardianModal}
				variant="info"
				customIconName="info-filled"
				headerTitle={alreadyRegisteredGuardianModal.headerTitle}
				title={alreadyRegisteredGuardianModal.title}
				description={
					<>
						{alreadyRegisteredGuardianModal.descriptionBeforeName}
						<Typography component="span" fontWeight="bold">
							{registeredGuardian?.name}
						</Typography>
						{alreadyRegisteredGuardianModal.descriptionAfterName}
					</>
				}
				footerContent={
					<Stack gap={1} width="100%">
						<Button
							onClick={handleCloseRegisteredGuardianModal}
							variant="contained"
							fullWidth
							size="large">
							{alreadyRegisteredGuardianModal.confirmButton}
						</Button>
						<Button
							onClick={handleTalkToSupport}
							variant="outlined"
							fullWidth
							size="large">
							{alreadyRegisteredGuardianModal.supportButton}
						</Button>
					</Stack>
				}
			/>
		</>
	);
}
